import { ArrowRight, Plus, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getPublicMRIMachines } from '../../services/apiService';
import { MRIMachinePopulated } from './types';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import LoadingRing from '../ui-components/LoadingRing';
import { FieldStrengthTile } from './FieldStrengthChip';

export default function AddMachine() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [machinesList, setMachinesList] = useState<MRIMachinePopulated[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const getData = async () => {
        setIsLoading(true);
        try {
            const res = await getPublicMRIMachines();
            const validMachines = Array.isArray(res)
                ? res.filter(
                      (m): m is MRIMachinePopulated => m != null && !!m._id
                  )
                : [];
            setMachinesList(validMachines);
        } catch (error) {
            console.error('Error fetching public machines:', error);
            setMachinesList([]);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        getData();
    }, []);

    const term = searchTerm.trim().toLowerCase();
    const filteredMachines = machinesList.filter(
        (machine) =>
            !term ||
            machine.Model?.toLowerCase().includes(term) ||
            machine.Manufacturer?.toLowerCase().includes(term) ||
            String(machine.FieldStrength).includes(term)
    );

    const createFromScratch = () =>
        navigate('/room-overview/create-room/create-new-modal');

    return (
        <div className="space-y-4">
            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle-foreground" />
                <Input
                    placeholder={t('roomOverview.addMachine.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                />
            </div>

            <p className="text-xs font-medium text-subtle-foreground">
                {t('roomOverview.addMachine.templatesCount', {
                    count: filteredMachines.length,
                })}
            </p>

            {isLoading ? (
                <div className="flex items-center justify-center py-8">
                    <LoadingRing size="large" />
                </div>
            ) : filteredMachines.length === 0 ? (
                <p className="py-8 text-center text-sm font-medium text-subtle-foreground">
                    {searchTerm
                        ? t('roomOverview.addMachine.noResults')
                        : t('roomOverview.addMachine.emptyState.title')}
                </p>
            ) : (
                <div className="grid max-h-[60vh] gap-3 overflow-y-auto pr-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 [scrollbar-color:hsl(var(--border-subtle))_transparent] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-subtle [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-2">
                    {filteredMachines.map((machine) => (
                        <Card
                            key={machine._id}
                            role="button"
                            tabIndex={0}
                            aria-label={machine.Model}
                            className="flex cursor-pointer flex-col gap-3 border-subtle p-4 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() =>
                                navigate(
                                    `/room-overview/create-room/${machine._id}`
                                )
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    navigate(
                                        `/room-overview/create-room/${machine._id}`
                                    );
                                }
                            }}
                        >
                            <div className="flex items-center gap-3">
                                <FieldStrengthTile
                                    fieldStrength={machine.FieldStrength}
                                />
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold tracking-tight">
                                        {machine.Model}
                                    </p>
                                    {machine.Manufacturer && (
                                        <p className="truncate text-xs font-medium text-subtle-foreground">
                                            {machine.Manufacturer}
                                        </p>
                                    )}
                                </div>
                            </div>
                            <span
                                aria-hidden="true"
                                className="flex items-center justify-end gap-1 text-xs font-medium text-subtle-foreground"
                            >
                                {t('roomOverview.addMachine.useTemplate')}
                                <ArrowRight className="h-3 w-3" />
                            </span>
                        </Card>
                    ))}
                </div>
            )}

            <div className="flex justify-center pt-2">
                <Button size="lg" onClick={createFromScratch}>
                    <Plus className="h-4 w-4" />
                    {t('roomOverview.addMachine.orSection.button')}
                </Button>
            </div>
        </div>
    );
}
